import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AccountPasswordUpdate from './AccountPasswordUpdate';
import Hideable from './Hideable';
import SignInButton from './SignInButton';
import Util from './Util';
import Payment from './Payment';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Container, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import Confirm from './Confirm';
import About from './About';
import Settings from './Settings';

const ACCOUNT_INFO = gql`
query AccountInfo {
  account {
    id
    name
    familyName
    givenName
    email
    purchase {
      label
      status
    }
  }
}
`
  ;

const UPDATE_ACCOUNT = gql`
mutation UpdateAccount($name:String, $familyName:String, $givenName:String, $email:String!) {
  updateAccount(input: {name:$name,familyName:$familyName,givenName:$givenName,email:$email}) {
    created
  }
}`;

const UPDATE_PASSWORD = gql`
mutation UpdatePasssword($password:String!) {
  updatePassword(password:$password)
}
`;

const CANCEL_SUBSCRIPTION = gql`mutation CancelSubscription { cancelSubscription { label } }`;

const DELETE_ACCOUNT = gql`mutation DeleteAccount { deleteAccount }`;

const LOGOUT = gql`
mutation Logout {
  logout
}`;

const logout = (client) => {
  client.mutate({
    mutation: LOGOUT,
    refetchQueries: ['Account', 'Alerts', 'FindPlace', 'GetCalendar', 'Places']
  }).then(data => {
    if (data.data.register === null) {
      console.log('Sign out failed');
    } else {
      console.log('Signed out');
      window.location.reload()
    }
  });
}

const Account = props => {
  const [values, setValues] = useState({ name: '', familyName: '', givenName: '', email: '' });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const { loading, error, data } = useQuery(ACCOUNT_INFO, {
    client: props.client,
    onCompleted: data => setValues(data.account)
  });
  const [powerMode, setPowerMode] = useState(Settings.isPowerMode());
  const [gpsEnabled, setGpsEnabled] = useState(Settings.isGpsEnabled());
  const [telemetryEnabled, setTelemetryEnabled] = useState(Settings.isTelemetryEnabled());

  if (!Util.hasSession()) {
    return (<Container><SignInButton /></Container>)
  }

  if (loading) {
    return (<Container>Loading...</Container>)
  }

  if (error) {
    return (<Container>{`Error loading account:  + ${error}`}</Container>)
  }

  const handleChange = e => {
    const { name, value } = e.target
    setValues({ ...values, [name]: value })
  }

  const activeAccount = data?.account?.purchase && data?.account?.purchase?.status !== 'INACTIVE';

  return (<Container sx={{}}>
    <Button variant='contained' onClick={() => logout(props.client)}>Sign Out</Button>
    <br /><br />
    <div>
      <Accordion>
        <AccordionSummary>
          <Typography component="span">App Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1} direction='column'>
            <FormControlLabel control={
              <Switch
                disabled={!props.account}
                checked={gpsEnabled}
                onChange={() => {
                  Settings.setGpsEnabled(!gpsEnabled, props.onError)
                  setGpsEnabled(!gpsEnabled)
                }}
              />
            } label="GPS" />
            <FormControlLabel control={
              <Switch
                disabled={!props.account}
                checked={telemetryEnabled}
                onChange={() => {
                  Settings.setTelemetryEnabled(!telemetryEnabled)
                  setTelemetryEnabled(!telemetryEnabled)
                }}
              />
            } label="Telemetry" />
            <FormControlLabel control={
              <Switch
                checked={powerMode}
                onChange={() => {
                  Settings.setPowerMode(!powerMode)
                  setPowerMode(!powerMode)
                }}
              />
            } label="Power mode" />
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary>
          <Typography component="span">User Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <form fluid="true" onSubmit={e => {
            e.preventDefault();
            updateAccount(props.client, values);
          }} >
            <Stack spacing={1}>
              <label>Email</label>
              <input name='email' placeholder='Email' value={values.email} onChange={handleChange} />
              <label>Name</label>
              <input name='name' placeholder='Name' value={values.name} onChange={handleChange} />
              <label>Given Name</label>
              <input name='givenName' placeholder='Given Name' value={values.givenName} onChange={handleChange} />
              <label>Family Name</label>
              <input name='familyName' placeholder='Family Name' value={values.familyName} onChange={handleChange} />
              <Button variant='contained'>Update</Button>
            </Stack>
          </form>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary>
          <Typography component="span">Plan</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Container style={{ textAlign: 'center' }}>
            <h3>Your Plan</h3>
            <Hideable hidden={activeAccount}>
              <b>You do not have an active account.</b>
              <Payment>
                <Link to="/account/purchase">Purchase a Plan</Link><br />
              </Payment>
            </Hideable>
            <Hideable hidden={!data.account?.purchase}>
              {data.account?.purchase?.label}<br />
              Status: {data.account?.purchase?.status}<br />
              <Button variant='contained'
                style={{ margin: '0 auto 0 auto' }}
                primary={true}
                onClick={() => setConfirmCancelOpen(true)}
                disabled={data.account?.purchase?.status === 'INACTIVE'}
              >Cancel</Button>
              <Confirm
                open={confirmCancelOpen}
                onCancel={() => setConfirmCancelOpen(false)}
                onConfirm={() => {
                  setConfirmCancelOpen(false);
                  cancelSubscription(props.client);
                }}
              />
            </Hideable>
          </Container>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary>
          <Typography component="span">Manage</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <AccountPasswordUpdate
              onSubmit={values => updatePassword(props.client, values)}
            />
            <br />
            <Button
              variant='contained'
              style={{ margin: '0 auto 0 auto' }}
              primary={true}
              onClick={() => setConfirmDeleteOpen(true)}
            >Delete Account</Button>
            <Confirm
              open={confirmDeleteOpen}
              onCancel={() => setConfirmDeleteOpen(false)}
              onConfirm={() => deleteAccount(props.client)}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary>
          <Typography component="span">About</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <About account={props.account} client={props.client} />
          </Box>
        </AccordionDetails>
      </Accordion>
    </div>
  </Container>);
}

const updateAccount = (client, values) => {
  console.log('Update account ', values);
  client.mutate({
    mutation: UPDATE_ACCOUNT,
    variables: values,
    refetchQueries: ['AccountInfo']
  }).then(data => {
    if (data.data.register === null) {
      console.log('Update account failed');
    }
  });
}

const updatePassword = (client, values) => {
  console.log('Update password ');
  if (!values.password) {
    return;
  }
  client.mutate({
    mutation: UPDATE_PASSWORD,
    variables: values,
    refetchQueries: ['AccountInfo']
  }).then(data => {
    if (data.data.updatePassword === null) {
      console.log('Update password failed');
    }
  });
}

const cancelSubscription = (client) => {
  console.log('Cancel subscription');
  client.mutate({
    mutation: CANCEL_SUBSCRIPTION,
    refetchQueries: ['Account', 'AccountInfo']
  }).then(data => {
    if (!data.data.cancelSubscription) {
      console.log('Cancel subscription failed');
    } else {
      console.log('Canceled subscription');
    }
  });
}

const deleteAccount = (client) => {
  console.log('Delete account');
  client.mutate({
    mutation: DELETE_ACCOUNT,
    refetchQueries: ['Account', 'AccountInfo']
  }).then(data => {
    if (!data.data.deleteAccount) {
      console.log('Delete account failed');
    } else {
      window.location.reload()
    }
  });
}

export default Account;